import { HalError } from "~/lib/hal";

// Set once a redirect fires. `_app.tsx`'s beforeLoad guard only catches a 401
// at route entry; a 401 that surfaces once a page is already mounted (a
// query or mutation whose own token refresh also failed (queryClient.ts), or
// a route that throws one straight from its loader/component
// (ErrorPage.tsx)) is seen by one of these two call sites instead, and
// several can fire in the same beat: every widget on a dashboard refetching
// at once, say. Without this flag each would issue its own full-page
// navigation; module state is fine for it, since it exists only until the
// navigation below unloads it (TMF-169).
let redirected = false;

/**
 * The one place a post-mount 401 is turned into a full-page trip to /login.
 * Returns whether it redirected, so a caller that renders UI (ErrorPage) can
 * skip that UI when it did.
 *
 * Tokens are already cleared by the time this runs (halFetchOnce clears them
 * before throwing), so a plain reload is correct and this needs no router
 * import: going through `router.navigate` here would mean this module
 * importing the router, which imports the routes, which import queryClient,
 * which imports this (TMF-169 plan, decision 1).
 */
export function redirectToLoginOnExpiredSession(error: unknown): boolean {
  if (!(error instanceof HalError) || !error.isUnauthorized) return false;
  if (redirected) return false;
  if (window.location.pathname === "/login") return false;

  redirected = true;
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(
    `/login?returnTo=${encodeURIComponent(returnTo)}&error=session_expired`,
  );
  return true;
}
